import {
  lazy,
  Suspense,
  useEffect,
  useId,
  useRef,
  useState,
  type DragEvent,
  type ReactNode,
} from 'react';
import { Banner, Button, Icon } from '../components';
import { IMAGE_TYPES, isImageType, type ImageFrame, type ImageGuide } from './crop';
import { FallbackImage } from './FallbackImage';

export type { ImageFrame, ImageGuide } from './crop';

// The cropper loads with the first image chosen, so it stays out of every other page.
const CropDialog = lazy(() => import('./CropDialog'));
const TYPES_IN_WORDS = 'JPEG, PNG, WebP or GIF';

/** An image field: choose or drop a file and crop it; the caller uploads what `onCrop` hands it. */
export function ImageUpload({
  label,
  required,
  optional,
  hint,
  error,
  src,
  fallbackSrc,
  alt = '',
  frame,
  guides,
  frameOverlay,
  cropTitle = 'Crop the image',
  uploading,
  refused,
  onRetry,
  onCrop,
  onRemove,
  disabled,
}: {
  label: string;
  required?: boolean;
  optional?: boolean;
  hint?: ReactNode;
  error?: ReactNode;
  /** The image the field holds now. */
  src?: string | null;
  /** Shown when `src` doesn't load, as the app falls back from its CDN copy. */
  fallbackSrc?: string | null;
  alt?: string;
  frame: ImageFrame;
  guides?: ImageGuide[];
  /** Drawn over the frame while cropping, such as the text the app lays on the image. */
  frameOverlay?: ReactNode;
  cropTitle?: string;
  uploading?: boolean;
  /** Why the last upload failed, in the server's words. */
  refused?: string;
  onRetry?: () => void;
  onCrop: (file: File) => void;
  onRemove?: () => void;
  disabled?: boolean;
}) {
  const id = useId();
  const input = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  const [wrongType, setWrongType] = useState<string | null>(null);
  const [picked, setPicked] = useState<{ url: string; file: File } | null>(null);
  const locked = disabled || uploading;

  useEffect(() => () => (picked ? URL.revokeObjectURL(picked.url) : undefined), [picked]);

  const choose = (file: File | undefined) => {
    if (!file || locked) return;
    if (!isImageType(file.type)) {
      setWrongType(`“${file.name}” isn't an image the app can show. Choose a ${TYPES_IN_WORDS}.`);
      return;
    }
    setWrongType(null);
    setPicked({ url: URL.createObjectURL(file), file });
  };

  const drop = (event: DragEvent) => {
    event.preventDefault();
    setOver(false);
    choose(event.dataTransfer.files[0]);
  };

  const pick = () => input.current?.click();
  const shownError = wrongType ?? error;

  return (
    <div
      className={shownError ? 'rq-field rq-image is-invalid' : 'rq-field rq-image'}
      role="group"
      aria-labelledby={`${id}-label`}
      aria-describedby={shownError ? `${id}-err` : hint ? `${id}-hint` : undefined}
      onDragOver={(event) => {
        event.preventDefault();
        if (!locked) setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={drop}
    >
      <span className="rq-field-label" id={`${id}-label`}>
        {label}
        {required ? (
          <span className="rq-field-req"> (required)</span>
        ) : optional ? (
          <span className="rq-field-opt"> (optional)</span>
        ) : null}
      </span>

      {src ? (
        <div className={over ? 'rq-image-held is-over' : 'rq-image-held'}>
          <div className="rq-image-frame" style={{ aspectRatio: String(frame.aspect) }}>
            <FallbackImage
              src={src}
              fallbackSrc={fallbackSrc}
              alt={alt}
              missing={
                <span className="rq-image-broken rq-missing">The image couldn't be loaded</span>
              }
            />
            {uploading ? (
              <span className="rq-image-busy" role="status">
                <Icon name="loader-circle" size={16} className="rq-spin" />
                Uploading…
              </span>
            ) : null}
          </div>
          <div className="rq-image-actions">
            <Button size="sm" icon="upload" onClick={pick} disabled={locked}>
              Replace…
            </Button>
            {onRemove ? (
              <Button size="sm" variant="quiet" icon="trash-2" onClick={onRemove} disabled={locked}>
                Remove
              </Button>
            ) : null}
          </div>
        </div>
      ) : (
        <div className={over ? 'rq-image-drop is-over' : 'rq-image-drop'}>
          <Icon name="upload" size={20} />
          <p className="rq-image-drop-title">Drop an image here</p>
          <p className="rq-image-drop-note">{TYPES_IN_WORDS}. You crop it to the frame next.</p>
          <Button icon="upload" onClick={pick} disabled={locked}>
            Choose an image…
          </Button>
        </div>
      )}

      <input
        ref={input}
        type="file"
        accept={IMAGE_TYPES.join(',')}
        hidden
        tabIndex={-1}
        onChange={(event) => {
          choose(event.target.files?.[0]);
          event.target.value = '';
        }}
      />

      {shownError ? (
        <p className="rq-field-error" id={`${id}-err`}>
          <Icon name="octagon-alert" size={14} />
          {shownError}
        </p>
      ) : hint ? (
        <p className="rq-field-hint" id={`${id}-hint`}>
          {hint}
        </p>
      ) : null}

      {refused && !uploading ? (
        <Banner
          tone="critical"
          title="Couldn't upload the image"
          reason={refused}
          action={
            onRetry ? (
              <Button size="sm" icon="refresh-cw" onClick={onRetry}>
                Try again
              </Button>
            ) : undefined
          }
        />
      ) : null}

      {picked ? (
        <Suspense fallback={null}>
          <CropDialog
            src={picked.url}
            source={picked.file}
            frame={frame}
            guides={guides}
            overlay={frameOverlay}
            title={cropTitle}
            onCancel={() => setPicked(null)}
            onDone={(file) => {
              setPicked(null);
              onCrop(file);
            }}
          />
        </Suspense>
      ) : null}
    </div>
  );
}
